import { gql, useQuery } from '@apollo/client';
import { BoxProps } from '@chakra-ui/react';
import * as React from 'react';

import { logError } from '@/imports/logging/ClientLogger';

import {
  Announcements_AnnouncementFeedV2,
  Announcements_AnnouncementFeedV2Variables,
  Announcements_AnnouncementFeedV2_announcementFeedV2_posts_announcement,
} from './graphql-types/Announcements_AnnouncementFeedV2';

import { AnnouncementsDisplay } from './components/AnnouncementsDisplay';

const LIMIT = 10;

const ANNOUNCEMENT_FEED = gql`
  query Announcements_AnnouncementFeedV2($input: AnnouncementFeedInput_V2!) {
    announcementFeedV2(input: $input) {
      posts {
        _id
        announcement {
          ...AnnouncementsDisplay_AnnouncementFragment
        }
      }
      hasMore
      cursor
    }
  }
  ${AnnouncementsDisplay.announcementsFragment}
`;

export const Announcements: React.FC<{ groupId?: string } & BoxProps> = ({
  groupId,
  ...boxProps
}) => {
  const { data, fetchMore } = useQuery<
    Announcements_AnnouncementFeedV2,
    Announcements_AnnouncementFeedV2Variables
  >(ANNOUNCEMENT_FEED, {
    variables: { input: { groupId, limit: LIMIT } },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const announcements = data?.announcementFeedV2.posts
    .map((post) => post.announcement)
    .filter(
      (
        announcement
      ): announcement is Announcements_AnnouncementFeedV2_announcementFeedV2_posts_announcement =>
        !!announcement
    );
  const cursor = data?.announcementFeedV2.cursor;

  const loadMore = React.useCallback(
    () =>
      fetchMore({
        variables: {
          input: {
            groupId,
            cursor,
            limit: LIMIT,
          },
        },
      }).catch((e) => logError(e)),
    [cursor, fetchMore, groupId]
  );

  if (!data || !announcements || announcements.length < 1) {
    return null;
  }

  return (
    <AnnouncementsDisplay
      announcements={announcements}
      groupId={groupId}
      loadMore={loadMore}
      hasMore={data.announcementFeedV2.hasMore}
      {...boxProps}
    />
  );
};
